import { useState, useEffect } from 'react'
import { Dialog, DialogPanel, DialogBackdrop, Textarea } from '@headlessui/react'
import { StarRatingInput } from '../StarRatingInput'
import Close from '../../assets/close.svg?react'
import { BlackActionButton } from '../buttons/BlackActionButton'
import { useCreateReview } from '../../queries/useReviews'

interface WriteReviewDialogProps {
  isOpen: boolean
  onClose: () => void
  fragranceId: string
  initialRating?: number
}

export const WriteReviewDialog = ({
  isOpen,
  onClose,
  fragranceId,
  initialRating = 0,
}: WriteReviewDialogProps) => {
  const [rating, setRating] = useState(initialRating)
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setRating(initialRating)
    }
  }, [isOpen, initialRating])

  const createReviewMutation = useCreateReview(fragranceId)

  const handleSubmit = () => {
    if (rating === 0) {
      setError('Please select a rating')
      return
    }
    if (text.trim() === '') {
      setError('Please write a review')
      return
    }

    setError(null)

    createReviewMutation.mutate(
      { rating, text: text.trim() },
      {
        onSuccess: () => {
          setIsSuccess(true)
        },
        onError: (error) => {
          console.error('Error submitting review:', error)
          setError('Failed to submit review. Please try again.')
        },
      }
    )
  }

  const handleClose = () => {
    setRating(0)
    setText('')
    setError(null)
    setIsSuccess(false)
    onClose()
  }



  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute cursor-pointer top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Close width={16} height={16} />
          </button>

          {isSuccess ? (
            <>
              {/* Success State */}
              <h2 className="text-xl font-semibold mb-2 mt-6">Review Submitted</h2>
              <p className="text-gray-500 text-sm mb-6">
                Thank you for sharing your experience! Your review has been published.
              </p>
              <div className="flex justify-end">
                <BlackActionButton
                  label="Done"
                  onClick={handleClose}
                />
              </div>
            </>
          ) : (
            <>
              {/* Title */}
              <h2 className="text-xl font-semibold mb-2 mt-6">Write a review</h2>

              {/* Description */}
              <p className="text-gray-500 text-sm mb-6">
                Share your experience and help others discover their perfect match.
                Whether it lingered for hours or vanished in minutes — every note,
                nuance, and memory makes a difference.
              </p>

              {/* Star Rating */}
              <div className="mb-6">
                <StarRatingInput
                  rating={rating}
                  onRatingChange={setRating}
                  size={32}
                />
              </div>

              {/* Text Area */}
              <Textarea
                value={text}
                maxLength={1200}
                onChange={(e) => setText(e.target.value)}
                placeholder="Share your scent experience"
                className="w-full h-32 p-3 mb-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              />

              {/* Error Message */}
              {error && (
                <p className="text-sm text-red-600 mb-4">{error}</p>
              )}

              {/* Submit Button */}
              <div className="flex justify-end">
                <BlackActionButton
                  label={createReviewMutation.isPending ? 'Submitting...' : 'Submit'}
                  onClick={handleSubmit}
                />
              </div>
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
